import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import api from "@/lib/api";
import { resolveBackendFileUrl } from "@/lib/fileUrls";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ShieldCheck, User, Trash, PencilSimple, FloppyDisk, X,
  CheckCircle, Plus, MagnifyingGlass, Warning, Briefcase,
} from "@phosphor-icons/react";

// ── Config ──────────────────────────────────────────────────────
const MODULES = [
  { key: "projects",     label: "Projects" },
  { key: "sales",        label: "Sales" },
  { key: "leads",        label: "Leads" },
  { key: "campaigns",    label: "Campaigns" },
  { key: "support",      label: "Support" },
  { key: "clients",      label: "Clients" },
  { key: "products",     label: "Products" },
  { key: "spec_writing", label: "Spec Writing" },
];

// Colour per access level — filled for W/R, muted for Hidden.
const LEVEL = {
  write:  { bg: "#16A34A", fg: "#FFFFFF", letter: "W", label: "Write" },
  read:   { bg: "#0055FF", fg: "#FFFFFF", letter: "R", label: "Read"  },
  hidden: { bg: "#F5F5F5", fg: "#A3A3A3", letter: "—", label: "Hidden" },
};

const ROLE_CHIP = {
  platform_admin:    "bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]",
  team_manager:      "bg-[#EEF2FF] text-[#4338CA] border-[#C7D2FE]",
  tech_development:  "bg-[#F0F9FF] text-[#0369A1] border-[#BAE6FD]",
  support_team:      "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0]",
  sales_marketing:   "bg-[#FFFBEB] text-[#D97706] border-[#FDE68A]",
  testers:           "bg-[#FAF5FF] text-[#7C3AED] border-[#DDD6FE]",
};

const isAdminLegacyRole = r => r === "platform_admin" || r === "delivery_admin";

// ── Single module access cell ───────────────────────────────────
function ModuleCell({ level, locked, onClick, label }) {
  const v = LEVEL[level] || LEVEL.hidden;
  return (
    <button
      onClick={onClick}
      disabled={locked}
      title={locked ? `${label}: full access (admin role)` : `${label}: ${v.label} — click to cycle`}
      className={`w-9 h-9 flex items-center justify-center text-xs font-black transition-all ${locked ? "cursor-default opacity-90" : "cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-[#0A0A0A]/30"}`}
      style={{ backgroundColor: v.bg, color: v.fg }}
    >
      {v.letter}
    </button>
  );
}

// ── Role chip strip — reads/writes via the Phase-2 API ──────────
function InlineRoleChips({ userId, currentUserIsPlatformAdmin, onChange }) {
  const [roles, setRoles] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [showPicker, setShowPicker] = useState(false);

  const load = async () => {
    try {
      const [a, all] = await Promise.all([
        api.get(`/users/${userId}/roles`),
        api.get(`/roles`),
      ]);
      setRoles(a.data.roles || []);
      setAllRoles(all.data.roles || []);
    } catch {}
  };
  useEffect(() => { load(); }, [userId]);

  const save = async (names) => {
    try {
      await api.put(`/users/${userId}/roles`, { role_names: names });
      toast.success("Roles updated");
      load();
      onChange?.();
      setShowPicker(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };
  const add = (name) => save([...roles.map(r => r.name), name]);
  const remove = (name) => save(roles.map(r => r.name).filter(n => n !== name));

  const assignedNames = new Set(roles.map(r => r.name));
  const available = allRoles.filter(r => !assignedNames.has(r.name));

  return (
    <div className="flex flex-wrap gap-1 items-center">
      {roles.map(r => {
        const canRemove = r.name === "platform_admin" ? currentUserIsPlatformAdmin : true;
        return (
          <span
            key={r.name}
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border ${ROLE_CHIP[r.name] || "bg-[#F5F5F5] text-[#525252] border-[#E5E5E5]"}`}
            data-testid={`role-chip-${userId}-${r.name}`}
          >
            {r.label}
            {canRemove && (
              <button onClick={() => remove(r.name)} className="ml-0.5 hover:opacity-70" title="Remove" data-testid={`role-remove-${userId}-${r.name}`}>
                <X size={9} weight="bold" />
              </button>
            )}
          </span>
        );
      })}

      {available.length > 0 && (
        <div className="relative inline-block">
          <button
            onClick={() => setShowPicker(v => !v)}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border border-dashed border-[#A3A3A3] text-[#525252] hover:border-[#0055FF] hover:text-[#0055FF] hover:bg-[#F0F7FF]"
            data-testid={`role-add-${userId}`}
          >
            <Plus size={9} weight="bold" /> Add
          </button>
          {showPicker && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
              <div className="absolute left-0 top-full mt-1 bg-white border border-[#E5E5E5] shadow-lg z-50 min-w-[220px]">
                {available.map(r => {
                  const isPA = r.name === "platform_admin";
                  const canGrant = isPA ? currentUserIsPlatformAdmin : true;
                  return (
                    <button
                      key={r.name}
                      onClick={() => canGrant && add(r.name)}
                      disabled={!canGrant}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-[#F5F5F5] last:border-b-0 ${canGrant ? "hover:bg-[#F5F5F5]" : "opacity-40 cursor-not-allowed"}`}
                      data-testid={`role-pick-${userId}-${r.name}`}
                    >
                      <div className="font-bold text-[#0A0A0A] flex items-center gap-1.5">
                        {r.label}
                        {!canGrant && <Warning size={10} className="text-[#D97706]" title="Only Platform Admins can grant this" />}
                      </div>
                      {r.description && <div className="text-[10px] text-[#A3A3A3] mt-0.5">{r.description}</div>}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Edit drawer — only shown when admin clicks "edit" on a row ──
// `viewerIsPlatformAdmin` controls whether the System Role dropdown is
// shown: only a platform_admin may promote/demote admins, and they can't
// change their own system role (backend guards this too).
function BulkProjectAssignDialog({ user: u, onClose, onSaved }) {
  const [projects, setProjects] = useState([]);
  const [existingMap, setExistingMap] = useState({}); // pid -> assignment row
  const [picked, setPicked] = useState(new Set());    // pids the admin checked this session
  const [search, setSearch] = useState("");
  const [perm, setPerm] = useState("read");           // 'read' | 'rw' | 'full'
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [pRes, aRes] = await Promise.all([
          api.get("/projects?status=active"),
          api.get(`/projects/team/by-user/${u.id}`),
        ]);
        setProjects(Array.isArray(pRes.data) ? pRes.data : []);
        const m = {};
        (aRes.data || []).forEach(a => { m[a.project_id] = a; });
        setExistingMap(m);
      } catch (e) {
        toast.error("Could not load projects");
      } finally { setLoading(false); }
    };
    load();
  }, [u.id]);

  const toggle = pid => {
    if (existingMap[pid]) return; // can't toggle existing — read-only here
    setPicked(s => {
      const n = new Set(s);
      if (n.has(pid)) n.delete(pid); else n.add(pid);
      return n;
    });
  };

  const filtered = projects.filter(p => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (p.name || "").toLowerCase().includes(q)
        || (p.tenant_name || "").toLowerCase().includes(q);
  });

  // Group by client/tenant for visual grouping.
  const grouped = {};
  filtered.forEach(p => {
    const key = p.tenant_name || "(No client)";
    (grouped[key] ||= []).push(p);
  });
  const tenantNames = Object.keys(grouped).sort();

  const save = async () => {
    if (picked.size === 0) { toast.info("Select at least one project"); return; }
    const flags = perm === "full" ? { can_create: true, can_read: true, can_update: true }
                 : perm === "rw"   ? { can_create: false, can_read: true, can_update: true }
                                   : { can_create: false, can_read: true, can_update: false };
    setSaving(true);
    try {
      const r = await api.post("/projects/team/bulk-assign", {
        user_id: u.id,
        project_ids: Array.from(picked),
        role: "member",
        ...flags,
      });
      const { assigned, skipped } = r.data;
      toast.success(`Assigned to ${assigned} project${assigned === 1 ? "" : "s"}${skipped ? ` (${skipped} already on team)` : ""}`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk assign failed");
    } finally { setSaving(false); }
  };

  const allOnTeamCount = Object.keys(existingMap).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-2xl max-h-[85vh] flex flex-col p-0" data-testid="bulk-assign-dialog">
        <DialogHeader className="p-5 border-b border-[#E5E5E5]">
          <DialogTitle className="font-bold text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            <Briefcase size={18} /> Assign {u.name || u.email} to Projects
          </DialogTitle>
          <p className="text-xs text-[#525252] mt-1">Currently on <strong>{allOnTeamCount}</strong> project{allOnTeamCount === 1 ? "" : "s"}. Pick more below — already-assigned projects are locked here (manage them from the project's Team tab).</p>
        </DialogHeader>

        {/* Permission preset */}
        <div className="px-5 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mr-1">Grant access:</span>
          {[
            { k: "read",  label: "Read only" },
            { k: "rw",    label: "Read + Update" },
            { k: "full",  label: "Full (Create + Read + Update)" },
          ].map(o => (
            <button key={o.k} onClick={() => setPerm(o.k)}
              className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider border ${perm === o.k ? "bg-[#0055FF] text-white border-[#0055FF]" : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#0055FF]"}`}
              data-testid={`bulk-perm-${o.k}`}>{o.label}</button>
          ))}
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-[#E5E5E5]">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
            <Input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search project or client name…"
              className="rounded-none pl-9 h-9 text-sm" data-testid="bulk-project-search" />
          </div>
        </div>

        {/* Project list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && <div className="text-center py-12 text-sm text-[#A3A3A3]"><div className="w-6 h-6 mx-auto border-2 border-[#0055FF] border-t-transparent animate-spin mb-2" />Loading…</div>}
          {!loading && tenantNames.length === 0 && (
            <div className="text-center py-12 text-sm text-[#A3A3A3]">No projects match your search.</div>
          )}
          {!loading && tenantNames.map(t => (
            <div key={t} className="mb-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] py-1 border-b border-[#F5F5F5] mb-1">{t}</div>
              {grouped[t].map(p => {
                const onTeam = !!existingMap[p.id];
                const checked = onTeam || picked.has(p.id);
                return (
                  <label key={p.id}
                    className={`flex items-center gap-3 px-2 py-2 text-sm border-b border-[#F5F5F5] ${onTeam ? "opacity-60 cursor-not-allowed" : "hover:bg-[#FAFBFF] cursor-pointer"}`}
                    data-testid={`bulk-project-row-${p.id}`}>
                    <input type="checkbox" checked={checked} disabled={onTeam}
                      onChange={() => toggle(p.id)}
                      className="w-4 h-4 accent-[#0055FF]" />
                    <span className="flex-1">{p.name}</span>
                    {onTeam && <span className="text-[10px] uppercase tracking-wider text-[#16A34A] font-bold">On team</span>}
                  </label>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#E5E5E5] flex items-center justify-between gap-2 bg-white">
          <div className="text-xs text-[#525252]">
            <strong className="text-[#0A0A0A]">{picked.size}</strong> new project{picked.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-none h-9 px-4">Cancel</Button>
            <Button onClick={save} disabled={saving || picked.size === 0}
              className="rounded-none h-9 px-4 bg-[#0055FF] hover:bg-[#003ECC] text-white font-bold"
              data-testid="bulk-assign-save">
              {saving ? "Saving…" : `Assign to ${picked.size} project${picked.size === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function EditUserDrawer({ user: u, onClose, onSaved, viewerIsPlatformAdmin, viewerId }) {
  const canEditSystemRole = viewerIsPlatformAdmin && viewerId !== u.id;
  // Collapse any legacy non-admin role label (developer, view_only,
  // project_manager…) to the canonical "Standard User" bucket for the UI
  // picker. On save we send `project_manager` as the safe non-admin value.
  const currentRoleKey =
    u.role === "platform_admin"  ? "platform_admin"  :
    u.role === "delivery_admin"  ? "delivery_admin"  : "standard";

  const [form, setForm] = useState({
    name: u.name || "",
    position: u.position || "",
    phone: u.phone || "",
    role: currentRoleKey,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name, position: form.position, phone: form.phone };
      // Only include `role` if the viewer is allowed AND they actually changed it.
      if (canEditSystemRole && form.role !== currentRoleKey) {
        payload.role = form.role === "standard" ? "project_manager" : form.role;
      }
      await api.put(`/users/${u.id}`, payload);
      toast.success("Profile updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-none border-[#E5E5E5] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-bold text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Edit {u.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-2" data-testid="edit-user-drawer">
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Name</label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1 text-sm" data-testid="edit-user-name" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Job Title</label>
            <Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} placeholder="e.g. Senior Solutions Engineer" className="rounded-none mt-1 text-sm" data-testid="edit-user-position" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase text-[#525252]">Phone</label>
            <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+14155550123" className="rounded-none mt-1 text-sm font-mono" data-testid="edit-user-phone" />
          </div>

          {canEditSystemRole && (
            <div className="pt-1">
              <label className="text-[10px] font-bold uppercase text-[#525252]">System Role</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                data-testid="edit-user-role"
                className="w-full border border-[#E5E5E5] h-9 px-3 text-sm mt-1 focus:outline-none focus:border-[#0055FF]"
              >
                <option value="standard">Standard User (no special privileges)</option>
                <option value="delivery_admin">Delivery Admin (full access, no FinOps/Billing)</option>
                <option value="platform_admin">Platform Admin (full access, incl. FinOps/Billing)</option>
              </select>
              <p className="text-[10px] text-[#A3A3A3] mt-1.5 leading-snug">
                Platform &amp; Delivery Admins automatically get full access to every module. Platform Admin is the <strong>only</strong> role that can see Billing, FinOps &amp; Renewals. Downgrading a Platform Admin to Standard will revoke their automatic bypass.
              </p>
            </div>
          )}

          <div className="text-[11px] text-[#A3A3A3] bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2">
            For standard users, access is controlled exclusively by the <strong>module access grid</strong> on this table — job title and Role Groups are informational only.
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={onClose} variant="outline" className="flex-1 rounded-none">Cancel</Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-none bg-[#0055FF] hover:bg-[#003ECC] text-white" data-testid="edit-user-save">
              <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ───────────────────────────────────────────────────
export default function TeamPage() {
  const { user, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [perms, setPerms] = useState({});   // userId -> { projects: 'write', ... }
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editing, setEditing] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(null); // user object

  const currentUserIsPlatformAdmin = hasRole("platform_admin");
  const isAdmin = currentUserIsPlatformAdmin || hasRole("delivery_admin");

  const loadUsers = async () => {
    try {
      const res = await api.get("/users?user_type=internal");
      // Delivery Admins must not see Platform Admin rows. Platform Admins
      // themselves see the full list. This is a UI filter — the backend
      // /users endpoint still enforces its own RBAC.
      let visible = res.data || [];
      if (!currentUserIsPlatformAdmin && hasRole("delivery_admin")) {
        visible = visible.filter(u => u.role !== "platform_admin");
      }
      setUsers(visible);
      // Parallel-fetch each user's permissions.
      const permsMap = {};
      await Promise.all(visible.map(async u => {
        try {
          const r = await api.get(`/users/${u.id}/permissions`);
          permsMap[u.id] = r.data.permissions || {};
        } catch { permsMap[u.id] = {}; }
      }));
      setPerms(permsMap);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  const cyclePerm = async (userId, module) => {
    const curr = perms[userId]?.[module] || "hidden";
    const next = curr === "write" ? "read" : curr === "read" ? "hidden" : "write";
    setPerms(p => ({ ...p, [userId]: { ...(p[userId] || {}), [module]: next } }));
    try {
      await api.put(`/users/${userId}/permissions`, { permissions: { [module]: next } });
    } catch {
      toast.error("Failed to update permissions");
      setPerms(p => ({ ...p, [userId]: { ...(p[userId] || {}), [module]: curr } }));
    }
  };

  const handleStatusChange = async (u) => {
    const next = u.status === "active" ? "disabled" : "active";
    try {
      await api.put(`/users/${u.id}`, { status: next });
      toast.success(`User ${next === "active" ? "activated" : "disabled"}`);
      loadUsers();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDeleteUser = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete}`);
      toast.success("User deleted");
      setConfirmDelete(null);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  if (user?.user_type !== "internal") {
    return (
      <div className="page-enter p-6 sm:p-8 text-center py-16">
        <ShieldCheck size={48} className="mx-auto text-[#E5E5E5] mb-4" />
        <p className="text-[#A3A3A3] font-medium">Access restricted to Eyvo internal users</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" />
      </div>
    );
  }

  // Filtered list.
  const q = search.trim().toLowerCase();
  let rows = users.filter(u =>
    !q || (u.name || "").toLowerCase().includes(q)
       || (u.email || "").toLowerCase().includes(q)
       || (u.position || "").toLowerCase().includes(q)
  );
  rows.sort((a, b) => {
    if (sort === "email") return (a.email || "").localeCompare(b.email || "");
    if (sort === "status") return (a.status || "").localeCompare(b.status || "");
    return (a.name || "").localeCompare(b.name || "");
  });

  const legacyRoleLabel = r => (r || "").replace(/_/g, " ");

  return (
    <div className="page-enter p-6 sm:p-8 space-y-5" data-testid="team-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Eyvo Team
          </h1>
          <p className="text-sm text-[#525252] mt-1">
            {users.length} internal user{users.length !== 1 ? "s" : ""} — new <code className="text-[11px] bg-[#F5F5F5] px-1">@eyvo.com</code> users are auto-created on first Google sign-in.
          </p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-[11px] text-[#525252] border border-[#E5E5E5] px-3 py-2">
          <span className="font-bold uppercase tracking-wider text-[10px] text-[#A3A3A3] mr-1">Legend</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-4 flex items-center justify-center text-[10px] font-black text-white" style={{backgroundColor:LEVEL.write.bg}}>W</span> Write</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-4 flex items-center justify-center text-[10px] font-black text-white" style={{backgroundColor:LEVEL.read.bg}}>R</span> Read</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-4 flex items-center justify-center text-[10px] font-black text-[#A3A3A3]" style={{backgroundColor:LEVEL.hidden.bg}}>—</span> Hidden</span>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email or job title…" className="rounded-none pl-9 h-9 text-sm" data-testid="team-search" />
        </div>
        <select value={sort} onChange={e => setSort(e.target.value)} className="border border-[#E5E5E5] h-9 px-3 text-xs font-bold uppercase tracking-wider focus:outline-none focus:border-[#0055FF]" data-testid="team-sort">
          <option value="name">Name A-Z</option>
          <option value="email">Email</option>
          <option value="status">Status</option>
        </select>
      </div>

      {/* Table */}
      <div className="border border-[#E5E5E5] bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5] text-[10px] uppercase tracking-wider text-[#525252]">
              <th className="px-3 py-2 text-left font-bold min-w-[260px]">Person</th>
              <th className="px-3 py-2 text-left font-bold min-w-[260px]">Role Groups</th>
              {MODULES.map(m => (
                <th key={m.key} className="px-2 py-2 text-center font-bold w-[60px]">{m.label}</th>
              ))}
              <th className="px-3 py-2 text-center font-bold w-[80px]">Status</th>
              {isAdmin && <th className="px-3 py-2 text-right font-bold w-[90px]"></th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={MODULES.length + 4} className="px-3 py-12 text-center text-[#A3A3A3]"><User size={28} className="mx-auto mb-2" /> No users match the filters.</td></tr>
            )}
            {rows.map(u => {
              const userPerms = perms[u.id] || {};
              const isTheirAdminRole = isAdminLegacyRole(u.role);
              return (
                <tr key={u.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFBFF]" data-testid={`team-user-${u.id}`}>
                  {/* Person: avatar + name + job title + email + phone + admin badge */}
                  <td className="px-3 py-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 bg-[#F5F5F5] flex items-center justify-center text-[11px] font-bold text-[#525252] flex-shrink-0 overflow-hidden rounded-full mt-0.5">
                        {u.avatar ? <img src={resolveBackendFileUrl(u.avatar)} alt="" className="w-full h-full object-cover" /> : u.name?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-bold text-[#0A0A0A] truncate">{u.name}</div>
                          {/* Legacy primary-role chip is now ONLY shown for the two admin roles.
                              Every other legacy value (view_only, developer, project_manager, …)
                              is obsolete — access is dictated solely by Role Groups + the matrix. */}
                          {isTheirAdminRole && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]" data-testid={`admin-chip-${u.id}`}>
                              {legacyRoleLabel(u.role)}
                            </span>
                          )}
                        </div>
                        {u.position && (
                          <div className="text-[11px] text-[#525252] truncate italic mt-0.5" data-testid={`user-position-${u.id}`}>{u.position}</div>
                        )}
                        <div className="text-[11px] text-[#525252] truncate">{u.email}</div>
                        <div className="text-[11px] text-[#A3A3A3] font-mono mt-0.5">{u.phone || "—"}</div>
                      </div>
                    </div>
                  </td>

                  {/* Role groups (Phase 2 multi-role) */}
                  <td className="px-3 py-3 align-middle">
                    <InlineRoleChips userId={u.id} currentUserIsPlatformAdmin={currentUserIsPlatformAdmin} onChange={loadUsers} />
                  </td>

                  {/* Module access cells */}
                  {MODULES.map(m => {
                    const level = isTheirAdminRole ? "write" : (userPerms[m.key] || "hidden");
                    return (
                      <td key={m.key} className="px-2 py-3 text-center" data-testid={`perm-${u.id}-${m.key}`}>
                        <ModuleCell
                          level={level}
                          locked={isTheirAdminRole || !isAdmin}
                          onClick={() => isAdmin && !isTheirAdminRole && cyclePerm(u.id, m.key)}
                          label={m.label}
                        />
                      </td>
                    );
                  })}

                  {/* Status */}
                  <td className="px-3 py-3 text-center">
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 border ${u.status === "active" ? "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0]" : "bg-[#FEF2F2] text-[#DC2626] border-[#FECACA]"}`}>
                      {u.status}
                    </span>
                  </td>

                  {/* Actions */}
                  {isAdmin && (
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => setBulkAssigning(u)} className="p-1 text-[#525252] hover:text-[#0055FF] hover:bg-[#F5F5F5]" title="Assign to projects" data-testid={`bulk-assign-${u.id}`}>
                          <Briefcase size={13} />
                        </button>
                        <button onClick={() => setEditing(u)} className="p-1 text-[#525252] hover:text-[#0055FF] hover:bg-[#F5F5F5]" title="Edit name / phone" data-testid={`edit-user-${u.id}`}>
                          <PencilSimple size={13} />
                        </button>
                        {u.id !== user?.id && (
                          <>
                            <button onClick={() => handleStatusChange(u)} className="p-1 text-[#525252] hover:text-[#0A0A0A] hover:bg-[#F5F5F5]" title={u.status === "active" ? "Disable" : "Enable"} data-testid={`toggle-status-${u.id}`}>
                              <CheckCircle size={13} />
                            </button>
                            <button onClick={() => setConfirmDelete(u.id)} className="p-1 text-[#DC2626] hover:bg-[#FEF2F2]" title="Delete" data-testid={`delete-team-user-${u.id}`}>
                              <Trash size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-[#A3A3A3]">
        Click a module cell to cycle <strong>Write → Read → Hidden</strong>. <strong>Platform Admin</strong> and <strong>Delivery Admin</strong> get automatic full access (cells locked); Delivery Admin is still blocked from Billing, FinOps &amp; Renewals. For everyone else, access is dictated <strong>only</strong> by the grid above — Role Groups are informational.
      </p>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-bold text-[#DC2626]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Delete Team Member?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#525252]">This will permanently remove this user. They can rejoin by signing in with Google again, but will lose their roles, permissions, and preferences.</p>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1 rounded-none h-10" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button className="flex-1 rounded-none h-10 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold" onClick={handleDeleteUser} data-testid="confirm-delete-team-user">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit drawer */}
      {editing && <EditUserDrawer user={editing} onClose={() => setEditing(null)} onSaved={loadUsers} viewerIsPlatformAdmin={currentUserIsPlatformAdmin} viewerId={user?.id} />}

      {/* Bulk-assign-to-projects dialog */}
      {bulkAssigning && <BulkProjectAssignDialog user={bulkAssigning} onClose={() => setBulkAssigning(null)} onSaved={loadUsers} />}
    </div>
  );
}
